import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Stethoscope, ClipboardList, Droplets, Waves, CalendarCheck, ArrowUpRight, MessageCircle, ShieldCheck } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import Seo from "@/components/Seo";
import { Reveal, Chapter } from "@/components/Reveal";
import { useSite } from "@/context/SiteContext";

const IMGS = {
  cells: "https://images.pexels.com/photos/11516492/pexels-photo-11516492.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1200",
  room: "https://images.pexels.com/photos/3736279/pexels-photo-3736279.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=800",
  science: "https://images.pexels.com/photos/37839843/pexels-photo-37839843.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=800",
};

const CONTENT = {
  en: {
    path: "/stem-cell-therapy",
    seoTitle: "Mesenchymal Stem Cell Therapy",
    seoDesc: "Physician-guided mesenchymal stem cell therapy in Tijuana, Mexico. Individualized evaluation, responsible protocols, and follow-up care at REVIVE Regenerative Medical Clinic.",
    chapter: "Mesenchymal Stem Cell Therapy",
    h1: "Regenerative Medicine Designed Around You",
    heroCopy: "REVIVE provides physician-guided mesenchymal stem cell therapy with an individualized approach focused on patient evaluation, safety, and responsible regenerative medicine.",
    ctaPrimary: "Schedule a Free Consultation",
    ctaSecondary: "Speak With Our Team on WhatsApp",
    bookingPrefill: "Stem Cell Consultation",
    mscChapter: "What Are Mesenchymal Stem Cells?",
    mscTitle: "Understanding the science, responsibly.",
    mscP: [
      "Mesenchymal stem cells (MSCs) are multipotent cells studied extensively in regenerative medicine for their biological signaling and immunomodulatory properties.",
      "Rather than simply traveling to an injured area and \"rebuilding\" damaged tissue, much of the scientific interest in MSCs involves the signals and biological factors they release, and how those signals interact with the body's own cellular environment.",
      "This is an active area of research. REVIVE clearly distinguishes established evidence from emerging or investigational applications, and no specific clinical outcome is guaranteed.",
    ],
    mscImgAlt: "Microscopic view of cells studied in regenerative medicine",
    processChapter: "The Process",
    processTitle: "How IV Stem Cell Therapy Works",
    steps: [
      { t: "Medical Evaluation", d: "Every patient begins with a medical consultation and review of their health history to determine whether further evaluation for treatment is appropriate." },
      { t: "Individualized Treatment Planning", d: "The REVIVE medical team reviews the patient's information and develops an appropriate treatment plan based on the patient's individual circumstances." },
      { t: "IV Administration", d: "When treatment is appropriate, mesenchymal stem cells are administered intravenously under medical supervision." },
      { t: "Biological Signaling", d: "After administration, MSCs interact with the body's biological environment and release signaling molecules that are being studied for their potential roles in inflammatory and regenerative processes." },
      { t: "Follow-Up", d: "Patients receive appropriate follow-up and guidance from the REVIVE team after treatment." },
    ],
    whyChapter: "Why Patients Explore Regenerative Medicine",
    whyTitle: "An active field of investigation.",
    whyP: [
      "Regenerative medicine is being investigated across numerous areas involving chronic, inflammatory, and degenerative conditions. Research interest continues to grow, and scientific understanding evolves over time.",
      "It is important to be clear: stem cell therapy is not presented as a cure, and it does not reverse, heal, or guarantee improvement for any disease. Whether any approach is appropriate depends on each patient's individual medical evaluation.",
    ],
    whyImgAlt: "Laboratory research in regenerative science",
    safetyTitle: "Patient Safety & Screening",
    safetyP1: "Not every patient is automatically a candidate for stem cell therapy. REVIVE performs an individualized medical evaluation and reviews relevant medical history before recommending any treatment.",
    safetyP2: "Treatment decisions are made by the medical team only after appropriate evaluation.",
    aboutChapter: "About Revive",
    aboutTitle: "Regenerative Medicine in Tijuana, Mexico",
    aboutP1: "REVIVE Regenerative Medical Clinic provides personalized regenerative medicine services in a modern clinical environment in Tijuana, Baja California, Mexico.",
    aboutP2: "Our approach combines medical evaluation, patient education, responsible treatment protocols, and individualized care.",
    aboutImgAlt: "REVIVE modern clinical environment in Tijuana",
    disclaimerLabel: "Important Medical Disclaimer",
    disclaimer: "The information provided on this website is for educational purposes only and is not intended to replace professional medical advice, diagnosis, or treatment. Regenerative medicine and mesenchymal stem cell therapies may be considered investigational or experimental for various conditions. Individual results vary, and no specific outcome or cure is guaranteed. Eligibility for treatment is determined following an appropriate medical evaluation.",
    finalTitle: "Discover Whether Regenerative Medicine May Be Right for You",
    finalSub: "Speak with the REVIVE team and schedule a personalized consultation.",
    finalWa: "Chat With Us on WhatsApp",
  },
  es: {
    path: "/es/terapia-con-celulas-madre",
    seoTitle: "Terapia con Células Madre Mesenquimales en Tijuana | REVIVE",
    seoDesc: "Conoce cómo funciona la terapia con células madre mesenquimales en REVIVE Regenerative Medical Clinic en Tijuana. Información sobre evaluación médica, administración intravenosa, señalización celular y nuestro enfoque personalizado.",
    chapter: "Terapia con Células Madre Mesenquimales",
    h1: "Medicina Regenerativa Diseñada para Ti",
    heroCopy: "En REVIVE ofrecemos terapia con células madre mesenquimales bajo supervisión médica y con un enfoque personalizado, basado en la evaluación del paciente, la seguridad y la práctica responsable de la medicina regenerativa.",
    ctaPrimary: "Agenda una Consulta Gratuita",
    ctaSecondary: "Habla con Nuestro Equipo por WhatsApp",
    bookingPrefill: "Consulta de Células Madre",
    mscChapter: "¿Qué Son las Células Madre Mesenquimales?",
    mscTitle: "Ciencia responsable, explicada con claridad.",
    mscP: [
      "Las células madre mesenquimales (CMM) son células multipotentes ampliamente estudiadas en medicina regenerativa por sus propiedades de señalización biológica e inmunomodulación.",
      "Más allá de la idea simplificada de que las células madre viajan a una zona lesionada y \"reconstruyen\" el tejido dañado, gran parte del interés científico en las CMM se centra en las señales y los factores biológicos que liberan, y en cómo interactúan con el entorno celular del propio cuerpo.",
      "Esta es un área de investigación activa. En REVIVE distinguimos con claridad la evidencia establecida de las aplicaciones emergentes o en investigación, y no garantizamos ningún resultado clínico específico.",
    ],
    mscImgAlt: "Vista microscópica de células estudiadas en medicina regenerativa",
    processChapter: "El Proceso",
    processTitle: "Cómo Funciona la Terapia Intravenosa con Células Madre",
    steps: [
      { t: "Evaluación Médica", d: "Cada paciente comienza con una consulta médica y una revisión de su historial de salud para determinar si es apropiado continuar con una evaluación para tratamiento." },
      { t: "Plan de Tratamiento Individualizado", d: "El equipo médico de REVIVE revisa la información del paciente y desarrolla un plan de tratamiento adecuado según sus circunstancias individuales." },
      { t: "Administración Intravenosa", d: "Cuando el tratamiento es apropiado, las células madre mesenquimales se administran por vía intravenosa bajo supervisión médica." },
      { t: "Señalización Biológica", d: "Tras la administración, las CMM interactúan con el entorno biológico del cuerpo y liberan moléculas de señalización que se están estudiando por su posible papel en los procesos inflamatorios y regenerativos." },
      { t: "Seguimiento", d: "Los pacientes reciben el seguimiento y la orientación adecuados por parte del equipo de REVIVE después del tratamiento." },
    ],
    whyChapter: "Por Qué los Pacientes Exploran la Medicina Regenerativa",
    whyTitle: "Un campo de investigación activo.",
    whyP: [
      "La medicina regenerativa se está investigando en numerosas áreas relacionadas con condiciones crónicas, inflamatorias y degenerativas. El interés científico continúa creciendo y el conocimiento evoluciona con el tiempo.",
      "Es importante ser claros: la terapia con células madre no se presenta como una cura y no revierte, sana ni garantiza mejoría para ninguna enfermedad. La idoneidad de cualquier enfoque depende de la evaluación médica individual de cada paciente.",
    ],
    whyImgAlt: "Investigación de laboratorio en ciencia regenerativa",
    safetyTitle: "Seguridad y Evaluación del Paciente",
    safetyP1: "No todos los pacientes son candidatos automáticamente para la terapia con células madre. REVIVE realiza una evaluación médica individualizada y revisa el historial médico relevante antes de recomendar cualquier tratamiento.",
    safetyP2: "Las decisiones de tratamiento las toma el equipo médico únicamente después de una evaluación apropiada.",
    aboutChapter: "Sobre Revive",
    aboutTitle: "Medicina Regenerativa en Tijuana, México",
    aboutP1: "REVIVE Regenerative Medical Clinic ofrece servicios personalizados de medicina regenerativa en un entorno clínico moderno en Tijuana, Baja California, México.",
    aboutP2: "Nuestro enfoque combina la evaluación médica, la educación del paciente, protocolos de tratamiento responsables y atención individualizada.",
    aboutImgAlt: "Entorno clínico moderno de REVIVE en Tijuana",
    disclaimerLabel: "Aviso Médico Importante",
    disclaimer: "La información proporcionada en este sitio web tiene fines exclusivamente educativos y no pretende sustituir el consejo, diagnóstico o tratamiento médico profesional. La medicina regenerativa y las terapias con células madre mesenquimales pueden considerarse de investigación o experimentales para diversas condiciones. Los resultados individuales varían y no se garantiza ningún resultado específico ni cura. La elegibilidad para el tratamiento se determina tras una evaluación médica apropiada.",
    finalTitle: "Descubre si la Medicina Regenerativa Puede Ser Adecuada para Ti",
    finalSub: "Habla con el equipo de REVIVE y agenda una consulta personalizada.",
    finalWa: "Conversa con Nosotros por WhatsApp",
  },
};

const stepIcons = [Stethoscope, ClipboardList, Droplets, Waves, CalendarCheck];

const StemCellTherapy = ({ es = false }) => {
  const { lang, langTick, openBooking, openWhatsApp } = useSite();
  const navigate = useNavigate();
  const c = es ? CONTENT.es : CONTENT.en;
  const other = es ? CONTENT.en : CONTENT.es;

  useEffect(() => {
    window.scrollTo(0, 0);
    if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
  }, []);

  // Language switcher keeps the visitor on the same page in the other language.
  // Only reacts to an explicit toggle click AFTER mount (langTick) — a stored
  // preference never redirects a direct visit (crawlers/shared links see this page).
  const prevLang = React.useRef(langTick);
  useEffect(() => {
    if (prevLang.current === langTick) return;
    prevLang.current = langTick;
    if ((lang === "es") !== es) navigate(other.path, { replace: true });
  }, [lang, langTick, es, navigate, other.path]);

  return (
    <div className="bg-offwhite min-h-screen" data-testid={es ? "msc-page-es" : "stem-cell-page"}>
      <Seo
        title={c.seoTitle}
        description={c.seoDesc}
        path={c.path}
        alternates={{
          en: "https://revivestem.com.mx/stem-cell-therapy",
          es: "https://revivestem.com.mx/es/terapia-con-celulas-madre",
          "x-default": "https://revivestem.com.mx/stem-cell-therapy",
        }}
      />
      <Navbar />

      {/* HERO */}
      <section className="relative bg-navy text-white pt-36 sm:pt-44 pb-20 sm:pb-28 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-[500px] h-[500px] rounded-full bg-champagne/5 blur-3xl" />
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <Reveal><Chapter><span className="text-champagne/60">{c.chapter}</span></Chapter></Reveal>
          <Reveal delay={0.05}>
            <h1 className="font-serif text-5xl sm:text-6xl lg:text-[5.5rem] tracking-tight leading-[1.05] mt-6 max-w-4xl">
              {c.h1}
            </h1>
          </Reveal>
          <Reveal delay={0.12}>
            <p className="font-sans text-white/65 text-lg sm:text-xl leading-relaxed mt-8 max-w-2xl">
              {c.heroCopy}
            </p>
          </Reveal>
          <Reveal delay={0.18}>
            <div className="flex flex-col sm:flex-row gap-4 mt-10">
              <button onClick={() => openBooking(c.bookingPrefill)}
                className="inline-flex items-center justify-center gap-2 rounded-full bg-white text-navy px-8 py-4 font-medium hover:bg-champagne transition-colors"
                data-testid="msc-hero-book">
                {c.ctaPrimary} <ArrowUpRight size={17} />
              </button>
              <button onClick={() => openWhatsApp()}
                className="inline-flex items-center justify-center gap-2 rounded-full bg-[#25D366] text-white px-8 py-4 font-medium hover:brightness-110 transition-[filter]"
                data-testid="msc-hero-whatsapp">
                <MessageCircle size={18} /> {c.ctaSecondary}
              </button>
            </div>
          </Reveal>
        </div>
      </section>

      {/* WHAT ARE MSCs */}
      <section className="py-24 sm:py-32">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16 grid lg:grid-cols-12 gap-12 lg:gap-20 items-start">
          <div className="lg:col-span-7">
            <Reveal><Chapter>{c.mscChapter}</Chapter></Reveal>
            <Reveal delay={0.05}>
              <h2 className="font-serif text-navy text-4xl sm:text-5xl tracking-tight mt-5 leading-tight">{c.mscTitle}</h2>
            </Reveal>
            <div className="mt-8 space-y-5 max-w-xl">
              {c.mscP.map((p, i) => (
                <Reveal key={i} delay={0.1 + i * 0.06}>
                  <p className="font-sans text-charcoal/70 text-base sm:text-lg leading-relaxed">{p}</p>
                </Reveal>
              ))}
            </div>
          </div>
          <div className="lg:col-span-5">
            <Reveal delay={0.15}>
              <div className="overflow-hidden rounded-2xl shadow-[0_20px_60px_rgba(15,37,55,0.15)]">
                <img src={IMGS.cells} alt={c.mscImgAlt} className="w-full h-[420px] object-cover" />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* HOW IV STEM CELL THERAPY WORKS — timeline */}
      <section className="bg-navy text-white py-24 sm:py-32 relative overflow-hidden">
        <div className="absolute -bottom-40 -left-40 w-[500px] h-[500px] rounded-full bg-champagne/5 blur-3xl" />
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <Reveal><Chapter><span className="text-champagne/60">{c.processChapter}</span></Chapter></Reveal>
          <Reveal delay={0.05}>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight mt-5 max-w-2xl leading-tight">{c.processTitle}</h2>
          </Reveal>
          <div className="mt-16 max-w-3xl">
            {c.steps.map((s, i) => {
              const Icon = stepIcons[i];
              return (
                <Reveal key={s.t} delay={i * 0.07}>
                  <div className="relative flex gap-6 sm:gap-8 pb-12 last:pb-0" data-testid={`msc-step-${i}`}>
                    {i < c.steps.length - 1 && <span className="absolute left-6 top-14 bottom-0 w-px bg-white/10" />}
                    <div className="w-12 h-12 rounded-full bg-champagne/15 text-champagne grid place-items-center shrink-0">
                      <Icon size={20} />
                    </div>
                    <div>
                      <p className="font-sans text-[11px] tracking-[0.2em] uppercase text-champagne/60">{String(i + 1).padStart(2, "0")}</p>
                      <h3 className="font-serif text-2xl sm:text-3xl mt-1">{s.t}</h3>
                      <p className="font-sans text-white/60 text-sm sm:text-base leading-relaxed mt-3 max-w-xl">{s.d}</p>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* WHY PATIENTS EXPLORE */}
      <section className="py-24 sm:py-32">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16 grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5 order-2 lg:order-1">
            <Reveal delay={0.15}>
              <div className="overflow-hidden rounded-2xl shadow-[0_20px_60px_rgba(15,37,55,0.15)]">
                <img src={IMGS.science} alt={c.whyImgAlt} className="w-full h-[380px] object-cover" />
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-7 order-1 lg:order-2">
            <Reveal><Chapter>{c.whyChapter}</Chapter></Reveal>
            <Reveal delay={0.05}>
              <h2 className="font-serif text-navy text-4xl sm:text-5xl tracking-tight mt-5 leading-tight">{c.whyTitle}</h2>
            </Reveal>
            <div className="mt-8 space-y-5 max-w-xl">
              {c.whyP.map((p, i) => (
                <Reveal key={i} delay={0.1 + i * 0.06}>
                  <p className="font-sans text-charcoal/70 text-base sm:text-lg leading-relaxed">{p}</p>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* PATIENT SAFETY & SCREENING */}
      <section className="pb-24 sm:pb-32">
        <div className="max-w-[1100px] mx-auto px-6 sm:px-10">
          <Reveal>
            <div className="rounded-3xl border-2 border-champagne/60 bg-white p-8 sm:p-12 shadow-[0_8px_30px_rgba(15,37,55,0.06)]" data-testid="msc-safety">
              <div className="flex items-start gap-5">
                <div className="w-12 h-12 rounded-full bg-navy text-champagne grid place-items-center shrink-0">
                  <ShieldCheck size={22} />
                </div>
                <div>
                  <h2 className="font-serif text-navy text-3xl sm:text-4xl tracking-tight">{c.safetyTitle}</h2>
                  <p className="font-sans text-charcoal/70 text-base sm:text-lg leading-relaxed mt-5">{c.safetyP1}</p>
                  <p className="font-sans text-charcoal/70 text-base sm:text-lg leading-relaxed mt-4">{c.safetyP2}</p>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ABOUT REVIVE */}
      <section className="pb-24 sm:pb-32">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <Reveal><Chapter>{c.aboutChapter}</Chapter></Reveal>
            <Reveal delay={0.05}>
              <h2 className="font-serif text-navy text-4xl sm:text-5xl tracking-tight mt-5 leading-tight">{c.aboutTitle}</h2>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="font-sans text-charcoal/70 text-base sm:text-lg leading-relaxed mt-8 max-w-xl">{c.aboutP1}</p>
            </Reveal>
            <Reveal delay={0.16}>
              <p className="font-sans text-charcoal/70 text-base sm:text-lg leading-relaxed mt-5 max-w-xl">{c.aboutP2}</p>
            </Reveal>
          </div>
          <div className="lg:col-span-5">
            <Reveal delay={0.15}>
              <div className="overflow-hidden rounded-2xl shadow-[0_20px_60px_rgba(15,37,55,0.15)]">
                <img src={IMGS.room} alt={c.aboutImgAlt} className="w-full h-[340px] object-cover" />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* MEDICAL DISCLAIMER */}
      <section className="pb-24">
        <div className="max-w-[1100px] mx-auto px-6 sm:px-10">
          <Reveal>
            <div className="rounded-2xl border border-navy/15 bg-champagne/10 p-7 sm:p-9" data-testid="msc-disclaimer">
              <p className="font-sans text-[11px] tracking-[0.2em] uppercase text-navy/60 mb-3">{c.disclaimerLabel}</p>
              <p className="font-sans text-charcoal/70 text-sm leading-relaxed">{c.disclaimer}</p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="bg-navy text-white py-24 sm:py-28 relative overflow-hidden">
        <div className="absolute -top-40 -right-40 w-[500px] h-[500px] rounded-full bg-champagne/5 blur-3xl" />
        <div className="max-w-[1100px] mx-auto px-6 sm:px-10 text-center">
          <Reveal>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-tight">{c.finalTitle}</h2>
          </Reveal>
          <Reveal delay={0.08}>
            <p className="font-sans text-white/60 text-lg mt-6">{c.finalSub}</p>
          </Reveal>
          <Reveal delay={0.14}>
            <div className="flex flex-col sm:flex-row gap-4 justify-center mt-10">
              <button onClick={() => openBooking(c.bookingPrefill)}
                className="inline-flex items-center justify-center gap-2 rounded-full bg-white text-navy px-8 py-4 font-medium hover:bg-champagne transition-colors"
                data-testid="msc-final-book">
                {c.ctaPrimary} <ArrowUpRight size={17} />
              </button>
              <button onClick={() => openWhatsApp()}
                className="inline-flex items-center justify-center gap-2 rounded-full bg-[#25D366] text-white px-8 py-4 font-medium hover:brightness-110 transition-[filter]"
                data-testid="msc-final-whatsapp">
                <MessageCircle size={18} /> {c.finalWa}
              </button>
            </div>
          </Reveal>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default StemCellTherapy;
